import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Gift, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from './ui/button';

const SEEN_KEY = 'sv-register-promo-seen';

const RegisterPromoModal = () => {
  const { language } = useLanguage();
  const { customer, loading } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Never show on admin or password-reset routes (its overlay would block those UIs)
  const hidden = location.pathname.startsWith('/admin') || location.pathname.startsWith('/reset-password');

  useEffect(() => {
    if (loading || customer || hidden) return;
    if (localStorage.getItem(SEEN_KEY) === 'true') return;
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      if (localStorage.getItem('age-verified') === 'true') {
        setOpen(true);
        clearInterval(iv);
      }
      if (tries > 80) clearInterval(iv);
    }, 500);
    return () => clearInterval(iv);
  }, [customer, loading, hidden]);

  const close = () => { localStorage.setItem(SEEN_KEY, 'true'); setOpen(false); };
  const goRegister = () => { localStorage.setItem(SEEN_KEY, 'true'); setOpen(false); navigate('/account'); };

  if (!open || hidden) return null;

  const hu = language === 'hu';

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4 animate-in fade-in duration-300"
      data-testid="register-promo-modal"
    >
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-amber-500/30 bg-zinc-950 shadow-2xl animate-in zoom-in-95 duration-300">
        {/* Glow accents */}
        <div className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 h-48 w-48 rounded-full bg-orange-600/20 blur-3xl" />

        <button
          onClick={close}
          className="absolute top-3 right-3 z-10 rounded-full p-1.5 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
          data-testid="register-promo-close"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="relative px-7 pt-9 pb-7 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 shadow-lg shadow-orange-500/30">
            <Gift className="h-8 w-8 text-black" />
          </div>

          <h2 className="mb-3 text-2xl font-bold text-white">
            {hu ? 'Kedves Vásárlónk!' : 'Dear Customer!'}
          </h2>

          <p className="mb-2 text-[15px] leading-relaxed text-zinc-300">
            {hu ? (
              <>
                Regisztrálj néhány másodperc alatt, és csatlakozz a{' '}
                <span className="font-semibold text-amber-400">hűségprogramunkhoz</span> — minél többet
                rendelsz, annál nagyobb <span className="font-semibold text-yellow-400">kedvezményt</span> kapsz!
              </>
            ) : (
              <>
                Register in seconds and join our{' '}
                <span className="font-semibold text-amber-400">loyalty program</span> — the more you order,
                the bigger <span className="font-semibold text-yellow-400">discount</span> you get!
              </>
            )}
          </p>

          <div className="my-5 flex items-center justify-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-400" />
            <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-orange-400 bg-clip-text text-3xl font-extrabold text-transparent drop-shadow">
              {hu ? 'akár 20% kedvezmény' : 'up to 20% off'}
            </span>
            <Sparkles className="h-5 w-5 text-yellow-400" />
          </div>

          <p className="mb-6 text-xs text-zinc-500">
            {hu
              ? 'Kövesd nyomon a rendeléseidet és gyűjtsd a hűségkedvezményeket. A ciklus minden 10 rendelés után újraindul.'
              : 'Track your orders and collect loyalty rewards. The cycle restarts every 10 orders.'}
          </p>

          <div className="flex flex-col gap-2.5">
            <Button
              onClick={goRegister}
              className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 py-6 font-bold text-black hover:from-yellow-300 hover:to-orange-400"
              data-testid="register-promo-cta"
            >
              {hu ? 'Regisztrálok most' : 'Register now'}
            </Button>
            <button
              onClick={close}
              className="text-sm text-zinc-500 transition-colors hover:text-zinc-300"
              data-testid="register-promo-later"
            >
              {hu ? 'Talán később' : 'Maybe later'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPromoModal;
